import { zodResolver } from "@hookform/resolvers/zod";
import { capitalCase } from "change-case";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { useToast } from "@smart/bridge-intake-components-dom";
import { track } from "@smart/bridge-metrics-dom";
import {
  appendFormLink,
  defaultTemplate,
  templateMessagePlaceholders,
  templatePlaceholderDetails,
} from "@smart/bridge-templates-basic";
import {
  submissionExtendedLabel,
  submissionStatusLabel,
} from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import {
  Alert,
  Editor,
  FieldList,
  Icon,
  Link,
  Modal,
  RadioButtons,
  richMessageFeatures,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { isNotNullOrUndefined, pluralise } from "@smart/itops-utils-basic";
import {
  submissionLink,
  useLoadSetting,
  useLoadTeam,
  useSendSubmission,
} from "@smart/manage-gql-client-dom";
import {
  communicationMethodIcon,
  communicationMethodLabel,
  useSendMessage,
  useAnalyticsEvent,
} from "@smart/manage-hooks-dom";

import { GqlSubmissionSummary } from "../../types";
import {
  useSubmissionsMatterTypes,
  useSubmissionsWithMatterAccess,
} from "../hooks";

type BulkSendReminderForm = {
  communicationMethod: "communicate";
  template: string;
};

export const BulkSendReminderModal = ({
  submissions,
  onClose,
}: {
  submissions: GqlSubmissionSummary[] | null;
  onClose: () => void;
}) => {
  const { "LOOP-1134": communicateExplanationLink } = useFeatureFlags();

  const [isLoading, setIsLoading] = useState(false);
  const [submissionsUrisWithoutEmail, setSubmissionsUrisWithoutEmail] =
    useState<string[]>([]);
  const [sendSubmission] = useSendSubmission();
  const { sendMessage } = useSendMessage();
  const { contacts } = useSmokeballApp();
  const { setToasts } = useToast();
  const team = useLoadTeam({});
  const setting = useLoadSetting({});
  const { publishEvent } = useAnalyticsEvent();

  const [columnFilters] = useStorage({
    defaultValue: [],
    key: "intake-pending-submissions-column-filters",
    schema: z.array(z.any()),
    storage: "session",
  });

  const findStoredFilter = (id: string) =>
    columnFilters.find((f) => f?.id === id)?.value;

  const initialTemplate = appendFormLink(
    setting.result?.defaultClientMessage?.reminder || defaultTemplate.reminder,
  );
  const form = useForm<BulkSendReminderForm>({
    defaultValues: {
      communicationMethod: "communicate",
      template: initialTemplate,
    },
    resolver: zodResolver(
      z.object({
        communicationMethod: z.enum(["communicate"]),
        template: z.string().min(1, "Please enter message"),
      }),
    ),
    mode: "onChange",
  });
  const { handleSubmit, control } = form;

  useEffect(() => {
    const checkSubmissionEmail = async () => {
      try {
        setIsLoading(true);
        if (!contacts || !submissions?.length) return;

        const urisWithoutEmail = (
          await Promise.all(
            submissions.map(async (s) => {
              const contactId = s.values.recipient?.contactId;
              const contact = contactId ? await contacts.get(contactId) : null;
              return contact?.person?.email || contact?.company?.email
                ? null
                : s.uri;
            }),
          )
        ).filter(isNotNullOrUndefined);

        if (urisWithoutEmail.length) {
          setSubmissionsUrisWithoutEmail(urisWithoutEmail);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubmissionEmail().catch(console.error);
  }, [contacts, submissions]);

  const {
    submissions: submissionsWithMatterAccess,
    noPermissionsMatterIds,
    loadingMatterPermissions,
  } = useSubmissionsWithMatterAccess(submissions);

  const { leadMatterTypes, matterMatterTypes, loadingMatterTypes } =
    useSubmissionsMatterTypes(submissions);

  const submissionsToBeSent = submissionsWithMatterAccess?.filter(
    (s) => !submissionsUrisWithoutEmail.includes(s.uri),
  );

  const onSubmit = handleSubmit(async ({ communicationMethod, template }) => {
    setIsLoading(true);

    try {
      const matterTypes = [
        ...(leadMatterTypes || []),
        ...(matterMatterTypes || []),
      ];
      await Promise.all(
        submissionsToBeSent?.map(async (s) => {
          const matterType = matterTypes.find(
            (mt) => mt.id === s.matter?.matterTypeId,
          );

          await sendSubmission({
            variables: {
              uri: s.uri,
              formUri: s.form.uri,
              communicationMethod,
              recipient: {
                contactId: s.values.recipient!.contactId,
                contactName: s.values.recipient!.contactName,
                email: s.values.recipient!.email,
                phone: s.values.recipient!.phone,
                cell: s.values.recipient!.cell,
              },
            },
            context: {
              stopOnManualSend: !!setting.result?.reminder?.stopOnManualSend,
            },
          });

          track("Submission Reminded", {
            submissionUri: s.uri,
            formUri: s.form.uri,
            matterCategory: s.matter?.isLead ? "lead" : "matter",
            matterTypeId: s.matter?.matterTypeId,
            matterTypeName: matterType?.name,
          });

          const contact =
            s.values.recipient!.contactId && contacts
              ? await contacts.get(s.values.recipient!.contactId)
              : undefined;

          const clientName = s.values.recipient!.contactName || "";
          const placeholderData = {
            clientFirstName: contact?.person?.firstName || "",
            clientLastName: contact?.person?.lastName || "",
            clientName,
            clientCustomName: contact?.person?.customName || clientName,
            formCreatedAt:
              s.values.createdAt || s.values.updatedAt
                ? formatDistanceToNow(
                    new Date(s.values.createdAt || s.values.updatedAt),
                    { addSuffix: true },
                  )
                : "",
            matterType: matterType?.name || "",
            firmName: team.result?.values.name || "Firm",
            firmPhone: team.result?.values.phone || "",
            clientEmail: s.values.recipient?.email || "",
            formTitle: s.form.values.title,
            formLink: submissionLink(s),
          };

          sendMessage({
            matterId: s.values.matterId || undefined,
            id: s.values.recipient!.contactId,
            email: s.values.recipient!.email,
            communicationMethod,
            placeholderData,
            subject: "",
            template: appendFormLink(template),
            showToast: false,
          });
        }) || [],
      );

      await publishEvent({
        category: "track",
        name: "IntakeBulkReminderSent",
        screen: "IntakeHomepage",
        entryPoint: "MainLandingPage",
        component: "SendButton",
        jiraKey: "LINK-2704",
        interactionType: "Active",
        properties: {
          SentVia: communicationMethodLabel[communicationMethod],
          Count: submissions?.length,
          DateSharedFilter: capitalCase(
            findStoredFilter("shared-at")?.period || "all time",
          ),
          TypeFilter: capitalCase(
            findStoredFilter("matter-category") || "both",
          ),
          ActivityStatusFilter: (findStoredFilter("status") || ["all"])
            .map((f: string) => {
              const statusLabel: Record<string, string> = {
                all: "All",
                draft: submissionStatusLabel.draft,
                prefilled: capitalCase(submissionExtendedLabel.internalUse),
                active: submissionStatusLabel.active,
              };
              return statusLabel[f];
            })
            .join(", "),
        },
      });

      setToasts([
        {
          palette: "success",
          text: `${submissionsToBeSent?.length} ${pluralise(
            submissionsToBeSent,
            "reminder",
          )} sent via the ${communicationMethodLabel[communicationMethod]}!`,
        },
      ]);
    } catch (error) {
      console.error(error);
      setToasts([
        {
          palette: "destructive",
          text: "Reminders couldn’t be sent. Please try again.",
        },
      ]);
    }
    setIsLoading(false);
    onClose();
  });

  const loading = isLoading || loadingMatterTypes || loadingMatterPermissions;

  return (
    <Modal
      header={{
        icon: "solidBell",
        iconBgColor: "bg-cyan-140",
        text: `Send Reminders (${submissions?.length})`,
      }}
      open={!!submissions?.length}
      onClose={onClose}
      footer={{
        buttons: [
          {
            text: "Cancel",
            type: "reset",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            text: `Send ${pluralise(submissionsToBeSent, "reminder")}`,
            onClick: onSubmit,
            variant: "primaryDefault",
            loading,
          },
        ],
      }}
      size="default"
      closeOptions={{ clickOutside: false }}
      loading={loading}
    >
      {!!noPermissionsMatterIds?.length && (
        <Alert
          content={
            <p>{`You do not have the necessary permissions for ${noPermissionsMatterIds.length} of the selected matters. Reminders will not be sent for ${pluralise(noPermissionsMatterIds, "this")} ${pluralise(noPermissionsMatterIds, "form")}.`}</p>
          }
          palette="warning"
          variant="section"
        />
      )}
      {!!submissionsUrisWithoutEmail?.length && (
        <Alert
          content={`${submissionsUrisWithoutEmail.length} ${pluralise(submissionsUrisWithoutEmail, "contact")} ${pluralise(submissionsUrisWithoutEmail, "is")} missing an email address, so ${pluralise(submissionsUrisWithoutEmail, "it")} will not receive a reminder.`}
          palette="warning"
          variant="section"
        />
      )}
      <FormProvider {...form}>
        <FieldList>
          <Controller
            control={control}
            name="communicationMethod"
            render={({ field, fieldState }) => (
              <RadioButtons
                id={field.name}
                label="Send via"
                error={!!fieldState.error}
                message={fieldState.error?.message}
                options={[
                  {
                    label: communicationMethodLabel.communicate,
                    value: "communicate",
                    prefix: (
                      <Icon
                        name={communicationMethodIcon.communicate!.name}
                        mask={communicationMethodIcon.communicate!.mask}
                        className={
                          communicationMethodIcon.communicate!.className
                        }
                      />
                    ),
                    detail: communicateExplanationLink && (
                      <Link
                        text={communicateExplanationLink.text || "Take a look"}
                        href={communicateExplanationLink.url}
                        target="_blank"
                      />
                    ),
                  },
                  {
                    label: communicationMethodLabel.email,
                    value: "email",
                    prefix: (
                      <Icon
                        name={communicationMethodIcon.email!.name}
                        className="text-neutral-400"
                      />
                    ),
                    detail: (
                      <Tooltip
                        text="Email reminders are not supported in bulk actions."
                        offsetBy={10}
                        className="flex items-center"
                      >
                        <Icon
                          name="regularCircleInfo"
                          className="text-black ml-4"
                        />
                      </Tooltip>
                    ),
                    disabled: true,
                  },
                ]}
                mandatory
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="template"
            render={({ field, fieldState }) => (
              <div className="flex max-h-[39.65rem] flex-col">
                <Editor
                  id={field.name}
                  label="Message"
                  error={!!fieldState.error}
                  message={fieldState.error?.message}
                  initialTextValue={initialTemplate}
                  onValueChange={field.onChange}
                  placeholders={{
                    keys: templateMessagePlaceholders,
                    details: templatePlaceholderDetails,
                    data: {
                      firmName: team.result?.values.name,
                      firmPhone: team.result?.values.phone,
                    },
                  }}
                  featureOptions={richMessageFeatures}
                  dataTestId="message-editor"
                  mandatory
                />
              </div>
            )}
          />
        </FieldList>
      </FormProvider>
    </Modal>
  );
};
